import { useCallback, useEffect, useRef, useState } from 'react'
import { toGatewayError, type GatewayError } from '@/utils/gatewayError'
import type {
  SentMessage,
  SentMessagePage,
} from '@/services/notificationService'

export const SENT_MESSAGES_PAGE_SIZE = 20

type Status = 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error'

type State = {
  status: Status
  items: SentMessage[]
  total: number
  /** The last page held in `items`. */
  page: number
  error: GatewayError | null
}

const IDLE: State = {
  status: 'idle',
  items: [],
  total: 0,
  page: 0,
  error: null,
}

export type SentMessagesList = State & {
  hasMore: boolean
  loadMore: () => void
  /** Repeats the request that failed. */
  retry: () => void
}

/**
 * A growing list of sent-message cards. A null `load` leaves the list idle;
 * a new `key` starts it over from page 1. The failure stays in the state so
 * the panel can say so, instead of showing an empty list.
 */
export const useSentMessages = (
  load: ((page: number) => Promise<SentMessagePage>) | null,
  key: string
): SentMessagesList => {
  const [state, setState] = useState<State>(IDLE)
  const loadRef = useRef(load)
  loadRef.current = load
  const latest = useRef(0)
  const failed = useRef({ page: 1, append: false })

  const fetchPage = useCallback((page: number, append: boolean) => {
    const run = loadRef.current
    if (!run) return
    const id = ++latest.current
    failed.current = { page, append }
    setState(prev => ({
      ...(append ? prev : IDLE),
      status: append ? 'loadingMore' : 'loading',
    }))
    run(page).then(
      result => {
        if (id !== latest.current) return
        setState(prev => ({
          status: 'ready',
          items: append ? [...prev.items, ...result.result] : result.result,
          total: result.total,
          page,
          error: null,
        }))
      },
      error => {
        if (id !== latest.current) return
        setState(prev => ({
          ...prev,
          status: 'error',
          error: toGatewayError(error, 'טעינת ההודעות נכשלה'),
        }))
      }
    )
  }, [])

  const enabled = load !== null
  useEffect(() => {
    if (!enabled) {
      latest.current++
      setState(IDLE)
      return
    }
    fetchPage(1, false)
  }, [enabled, key, fetchPage])

  return {
    ...state,
    hasMore: state.status === 'ready' && state.items.length < state.total,
    loadMore: () => fetchPage(state.page + 1, true),
    retry: () => fetchPage(failed.current.page, failed.current.append),
  }
}
